import { PayloadAction, createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import * as nutritionPlanService from '../../../service/nutrition-plan.service'
import { MealItemProgressRequest, MealItemProgressResponse, MemberNutritionDayResponse, NutritionPlan, NutritionPlanRequest } from "../../../service/model/nutrition.model";
import { showNotification } from "../../alerts/alert.slice";
import { NotificationType } from "../../../shared/enums";
import { getAxiosErrorMessage } from "../../../service/axios/axios.call.service";

export interface NutritionPlanState {
    isLoading: boolean;
    nutritionPlanDay?: MemberNutritionDayResponse;
    activePlan?: NutritionPlan;
}

const nutritionPlansAdapter = createEntityAdapter({
    selectId: (nutritionPlan: NutritionPlan) => nutritionPlan.id,
    sortComparer: (a, b) => a.startDate?.localeCompare(b.startDate),
  })

export const initialState = nutritionPlansAdapter.getInitialState({
    isLoading: false,
} as NutritionPlanState);

export const fetchNutritionPlans = createAsyncThunk<NutritionPlan[], number>(
    'nutritionPlans/all',
    async (memberId, {rejectWithValue}) => {
        return nutritionPlanService.getNutritionPlans(memberId).catch((errors) => {
            return rejectWithValue(errors);
        })
    }
);

// export const fetchTodayPlan = createAsyncThunk<DaySnapshot, number>(
//     'nutritionPlans/today',
//     async (memberId, {rejectWithValue}) => {
//         return nutritionPlanService.getTodayPlan(memberId).catch((errors) => {
//             return rejectWithValue(errors);
//         })
//     }
// );

export const fetchNutritionPlanForDay = createAsyncThunk<MemberNutritionDayResponse, { day: string }>(
    'nutritionPlans/day',
    async ({ day }, { rejectWithValue }) => {
        return nutritionPlanService.getNutritionPlanDay(day).catch((errors) => {
            return rejectWithValue(errors);
        })
    }
);

export const fetchActivePlan = createAsyncThunk<NutritionPlan, number>(
    'nutritionPlans/active',
    async (memberId, {rejectWithValue}) => {
        return nutritionPlanService.getActivePlan(memberId).catch((errors) => {
            return rejectWithValue(errors);
        })
    }
);

export const createNutritionPlan = createAsyncThunk<
    NutritionPlan,
    NutritionPlanRequest
>('nutritionPlans/create', async (request, { rejectWithValue, dispatch }) => {
    return nutritionPlanService
        .createNutritionPlan(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Nutrition plan created successfully',
                    type: NotificationType.Success,
                })
            );
            dispatch(fetchActivePlan(request.memberId));
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                })
            );
            return rejectWithValue(errors);
        });
});

export const updateNutritionPlan = createAsyncThunk<
    NutritionPlan,
    NutritionPlanRequest
>('nutritionPlans/update', async (request, { rejectWithValue, dispatch }) => {
    return nutritionPlanService
        .updateNutritionPlan(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Nutrition plan updated successfully',
                    type: NotificationType.Success,
                })
            );
            dispatch(fetchActivePlan(request.memberId));
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                })
            );
            return rejectWithValue(errors);
        });
});

export const deleteNutritionPlan = createAsyncThunk<
    number,
    NutritionPlan
>('nutritionPlans/delete', async (plan, { rejectWithValue, dispatch }) => {
    return nutritionPlanService
        .deleteNutritionPlan(plan.id)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Nutrition plan deleted successfully',
                    type: NotificationType.Success,
                })
            );
            dispatch(fetchNutritionPlans(plan.memberId));
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                })
            );
            return rejectWithValue(errors);
        });
});

export const updateMealItemProgress = createAsyncThunk<
    MealItemProgressResponse,
    MealItemProgressRequest
>('nutritionPlans/meal-item-progress', async (request, { rejectWithValue, dispatch }) => {
    return nutritionPlanService
        .updateMealItemProgress(request)
        .then((response) => {
            dispatch(fetchNutritionPlanForDay({ day: request.progressDate }));
            dispatch(
                showNotification({
                    message: 'Meal item progress updated successfully',
                    type: NotificationType.Success,
                })
            );
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                })
            );
            return rejectWithValue(errors);
        });
});

const nutritionPlanSlice = createSlice({
    name: 'nutrition-plans',
    initialState,
    reducers: {
        resetNutritionPlan: (state) => {
            state.activePlan = undefined;
            state.nutritionPlanDay = undefined;
        },
        updateActivePlan: (state, action: PayloadAction<NutritionPlan>) => {
            state.activePlan = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
        .addCase(fetchNutritionPlans.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(fetchNutritionPlans.fulfilled, (state, action: PayloadAction<NutritionPlan[]>) => {
            nutritionPlansAdapter.setAll(state, action.payload);
            state.isLoading = false;
        })
        .addCase(fetchNutritionPlans.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(fetchNutritionPlanForDay.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(fetchNutritionPlanForDay.fulfilled, (state, action: PayloadAction<MemberNutritionDayResponse>) => {
            state.nutritionPlanDay = action.payload;
            state.isLoading = false;
        })
        .addCase(fetchNutritionPlanForDay.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(fetchActivePlan.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(fetchActivePlan.fulfilled, (state, action: PayloadAction<NutritionPlan>) => {
            state.activePlan = action.payload;
            state.isLoading = false;
        })
        .addCase(fetchActivePlan.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(createNutritionPlan.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(createNutritionPlan.fulfilled, (state) => {
            state.isLoading = false;
        })
        .addCase(createNutritionPlan.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(updateNutritionPlan.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(updateNutritionPlan.fulfilled, (state) => {
            state.isLoading = false;
        })
        .addCase(updateNutritionPlan.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(deleteNutritionPlan.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(deleteNutritionPlan.fulfilled, (state) => {
            state.isLoading = false;
        })
        .addCase(deleteNutritionPlan.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(updateMealItemProgress.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(updateMealItemProgress.fulfilled, (state) => {
            state.isLoading = false;
        })
        .addCase(updateMealItemProgress.rejected, (state) => {
            state.isLoading = false;
        });
    }
});

export const {
    selectAll: selectNutritionPlans,
    selectById: selectNutritionPlanById
} = nutritionPlansAdapter.getSelectors<RootState>((state) => state.nutritionPlans);

export const {
    resetNutritionPlan,
    updateActivePlan
} = nutritionPlanSlice.actions;

export const selectIsLoading = (state: RootState) => state.nutritionPlans.isLoading;
export const selectNutritionPlanDay = (state: RootState) => state.nutritionPlans.nutritionPlanDay;
export const selectActivePlan = (state: RootState) => state.nutritionPlans.activePlan;

export default nutritionPlanSlice.reducer;